import React, { ChangeEvent } from 'react';

import clsx from 'clsx';
import { Control } from 'react-hook-form';
import InputMask from 'react-input-mask';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';

import InputError from 'components/Inputs/InputError';
import UseFormControlWrapper from 'components/UseFormControllWrapper';
import { DateFormat } from 'enums/DateFormat';
import { ChangeFirstLetterToUppercase } from 'utils/stringUtils';
import { getDateFromFormat, isValidDate } from 'utils/dateUtils';

import styles from './DatePicker.module.scss';
import { DATE_PICKER_MIN_YEAR } from './datePickerInput.constants';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface DatePickerNewInputWrappedProps {
  name: string;
  format: DateFormat;
  mask: string;
  maxDate: Date | string;
  minDate: Date | string;
  control?: Control<any>;
  required?: boolean;
  label?: string;
  errorMessage?: string;
  value?: string;
  noInteraction?: boolean | null;
  className?: string;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  validate?: (value: string) => boolean | string;
}

const DatePickerInput = ({
  name,
  format,
  mask,
  control,
  required,
  label,
  errorMessage,
  value,
  minDate,
  maxDate,
  noInteraction,
  className,
  onChange,
  onBlur,
  disabled,
  validate,
}: DatePickerNewInputWrappedProps) => {
  const handleValidation = (currentValue: string) => {
    const inputDate = getDateFromFormat(currentValue, format);
    const min = getDateFromFormat(minDate, format);
    const max = getDateFromFormat(maxDate, format);
    const isValid = isValidDate(currentValue, format);
    const isEqualOrAfter = inputDate.isSameOrAfter(max);
    const isEqualOrBefore = inputDate.isSameOrBefore(min);
    const year = inputDate.year();

    if (!isValid || year < DATE_PICKER_MIN_YEAR) {
      return 'Please enter a valid date';
    }

    if (isEqualOrBefore) {
      return `Must be later than ${min.format(format)}`;
    }

    if (isEqualOrAfter) {
      return `Must be earlier than ${max.format(format)}`;
    }

    if (validate) {
      return validate(currentValue);
    }

    return true;
  };

  return (
    <UseFormControlWrapper name={name} control={control} label={label} required={required} validate={handleValidation}>
      <>
        <div className={clsx(styles.inputContainer, className)}>
          <label className={clsx(styles.label)}>{ChangeFirstLetterToUppercase(label)}</label>
          <InputMask
            name={name}
            mask={mask}
            placeholder={format.toUpperCase()}
            maskPlaceholder={format.replace(/\D/g, '_')}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            className={clsx(styles.input, {
              [styles.numberInput]: !errorMessage,
              [styles.numberInputError]: errorMessage,
              [styles.noInteractionText]: noInteraction,
            })}
            disabled={disabled}
          />
          {!!errorMessage && <InputError errorMessage={errorMessage} />}
        </div>
      </>
    </UseFormControlWrapper>
  );
};

export default DatePickerInput;
